/**
 * Driver Detail Page
 * ==================
 * صفحة تفاصيل السائق الشاملة
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTenantAuth } from '../../context/TenantAuthContext';
import { useToast } from '../../context/ToastContext';
import '../shared/PageStyles.css';
import SAR from '../../../components/SAR';

const I = {
  back: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>,
  star: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>,
  phone: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
  mail: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
  doc: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>,
  car: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a1 1 0 0 0-.8-.4H5.24a2 2 0 0 0-1.8 1.1l-.8 1.63A6 6 0 0 0 2 12.42V16h2"/><circle cx="6.5" cy="16.5" r="2.5"/><circle cx="16.5" cy="16.5" r="2.5"/></svg>,
  clock: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  map: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="10" r="3"/><path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 1 0-16 0c0 3 2.7 7 8 11.7z"/></svg>,
  device: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>,
  order: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  alert: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
  x: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
};

const DriverDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { apiCall } = useTenantAuth();
  const { success, error: showError } = useToast();

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [driver, setDriver] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [availability, setAvailability] = useState(null);
  
  // New features state
  const [incentives, setIncentives] = useState([]);
  const [incentivesSummary, setIncentivesSummary] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [location, setLocation] = useState(null);
  const [locationHistory, setLocationHistory] = useState([]);

  // Modals state
  const [showDocModal, setShowDocModal] = useState(false);
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [showShiftModal, setShowShiftModal] = useState(false);
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [availableShifts, setAvailableShifts] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Document form
  const [docForm, setDocForm] = useState({
    document_type: 'national_id',
    document_number: '',
    issue_date: '',
    expiry_date: '',
    file_url: '',
    notes: ''
  });

  // Vehicle form
  const [vehicleForm, setVehicleForm] = useState({
    vehicle_id: '',
    role: 'primary'
  });

  // Shift form
  const [shiftForm, setShiftForm] = useState({
    shift_id: '',
    days_of_week: []
  });

  const loadDriver = useCallback(async () => {
    setLoading(true);
    try {
      const [profile, statsData, availData] = await Promise.all([
        apiCall(`/tenant/drivers/${id}/full-profile`),
        apiCall(`/tenant/drivers/${id}/stats`),
        apiCall(`/tenant/drivers/${id}/availability`)
      ]);
      setDriver(profile);
      setStats(statsData);
      setAvailability(availData);
    } catch (err) {
      showError('فشل تحميل بيانات السائق');
    } finally {
      setLoading(false);
    }
  }, [apiCall, id, showError]);

  const loadDocuments = useCallback(async () => {
    try {
      const data = await apiCall(`/tenant/drivers/${id}/documents`);
      setDocuments(data || []);
    } catch (err) { console.error(err); }
  }, [apiCall, id]);

  const loadVehicles = useCallback(async () => {
    try {
      const data = await apiCall(`/tenant/drivers/${id}/vehicles`);
      setVehicles(data || []);
    } catch (err) { console.error(err); }
  }, [apiCall, id]);

  const loadShifts = useCallback(async () => {
    try {
      const data = await apiCall(`/tenant/drivers/${id}/shifts`);
      setShifts(data || []);
    } catch (err) { console.error(err); }
  }, [apiCall, id]);

  const loadReviews = useCallback(async () => {
    try {
      const data = await apiCall(`/tenant/drivers/${id}/reviews`);
      setReviews(data || []);
    } catch (err) { console.error(err); }
  }, [apiCall, id]);

  const loadActivityLogs = useCallback(async () => {
    try {
      const data = await apiCall(`/tenant/drivers/${id}/activity-logs`);
      setActivityLogs(data || []);
    } catch (err) { console.error(err); }
  }, [apiCall, id]);

  // Load incentives
  const loadIncentives = useCallback(async () => {
    try {
      const [list, summary] = await Promise.all([
        apiCall(`/tenant/drivers/${id}/incentives`),
        apiCall(`/tenant/drivers/${id}/incentives/summary`)
      ]);
      setIncentives(list || []);
      setIncentivesSummary(summary);
    } catch (err) { console.error(err); }
  }, [apiCall, id]);

  // Load leaves
  const loadLeaves = useCallback(async () => {
    try {
      const [list, balance] = await Promise.all([
        apiCall(`/tenant/drivers/${id}/leaves`),
        apiCall(`/tenant/drivers/${id}/leaves/balance`)
      ]);
      setLeaves(list || []);
      setLeaveBalance(balance);
    } catch (err) { console.error(err); }
  }, [apiCall, id]);

  // Load location
  const loadLocation = useCallback(async () => {
    try {
      const [loc, history] = await Promise.all([
        apiCall(`/tenant/drivers/${id}/location`),
        apiCall(`/tenant/drivers/${id}/location/history`)
      ]);
      setLocation(loc);
      setLocationHistory(history || []);
    } catch (err) { console.error(err); }
  }, [apiCall, id]);

  // Load available vehicles for assignment
  const loadAvailableVehicles = useCallback(async () => {
    try {
      const data = await apiCall('/tenant/vehicles?status=active');
      setAvailableVehicles(data.items || data || []);
    } catch (err) { console.error(err); }
  }, [apiCall]);

  // Load available shifts
  const loadAvailableShifts = useCallback(async () => {
    try {
      const data = await apiCall('/tenant/drivers/shifts/available');
      setAvailableShifts(data || []);
    } catch (err) { console.error(err); }
  }, [apiCall]);

  // Submit document
  const handleAddDocument = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await apiCall(`/tenant/drivers/${id}/documents`, {
        method: 'POST',
        body: JSON.stringify(docForm)
      });
      success('تم إضافة الوثيقة بنجاح');
      setShowDocModal(false);
      setDocForm({ document_type: 'national_id', document_number: '', issue_date: '', expiry_date: '', file_url: '', notes: '' });
      loadDocuments();
    } catch (err) {
      showError(err.message || 'فشل إضافة الوثيقة');
    } finally {
      setSubmitting(false);
    }
  };

  // Submit vehicle assignment
  const handleAssignVehicle = async (e) => {
    e.preventDefault();
    if (!vehicleForm.vehicle_id) {
      showError('الرجاء اختيار مركبة');
      return;
    }
    setSubmitting(true);
    try {
      await apiCall(`/tenant/drivers/${id}/vehicles`, {
        method: 'POST',
        body: JSON.stringify(vehicleForm)
      });
      success('تم تعيين المركبة بنجاح');
      setShowVehicleModal(false);
      setVehicleForm({ vehicle_id: '', role: 'primary' });
      loadVehicles();
    } catch (err) {
      showError(err.message || 'فشل تعيين المركبة');
    } finally {
      setSubmitting(false);
    }
  };

  // Submit shift assignment
  const handleAssignShift = async (e) => {
    e.preventDefault();
    if (!shiftForm.shift_id) {
      showError('الرجاء اختيار وردية');
      return;
    }
    setSubmitting(true);
    try {
      await apiCall(`/tenant/drivers/${id}/shifts`, {
        method: 'POST',
        body: JSON.stringify(shiftForm)
      });
      success('تم تعيين الوردية بنجاح');
      setShowShiftModal(false);
      setShiftForm({ shift_id: '', days_of_week: [] });
      loadShifts();
    } catch (err) {
      showError(err.message || 'فشل تعيين الوردية');
    } finally {
      setSubmitting(false);
    }
  };

  // Open vehicle modal and load vehicles
  const openVehicleModal = () => {
    loadAvailableVehicles();
    setShowVehicleModal(true);
  };

  // Open shift modal and load shifts
  const openShiftModal = () => {
    loadAvailableShifts();
    setShowShiftModal(true);
  };

  useEffect(() => { loadDriver(); }, [loadDriver]);

  useEffect(() => {
    if (activeTab === 'documents') loadDocuments();
    if (activeTab === 'vehicles') loadVehicles();
    if (activeTab === 'shifts') loadShifts();
    if (activeTab === 'reviews') loadReviews();
    if (activeTab === 'activity') loadActivityLogs();
    if (activeTab === 'incentives') loadIncentives();
    if (activeTab === 'leaves') loadLeaves();
    if (activeTab === 'location') loadLocation();
  }, [activeTab, loadDocuments, loadVehicles, loadShifts, loadReviews, loadActivityLogs, loadIncentives, loadLeaves, loadLocation]);

  const getStatusBadge = (status) => {
    const map = {
      active: { label: 'نشط', class: 'success' },
      suspended: { label: 'معلق', class: 'warning' },
      blocked: { label: 'محظور', class: 'danger' },
      pending: { label: 'قيد المراجعة', class: 'info' }
    };
    const s = map[status] || { label: status, class: 'default' };
    return <span className={`badge badge--${s.class}`}>{s.label}</span>;
  };

  const getAvailabilityBadge = (status) => {
    const map = {
      available: { label: 'متفرغ', class: 'success' },
      on_delivery: { label: 'في توصيل', class: 'primary' },
      on_break: { label: 'استراحة', class: 'warning' },
      off_duty: { label: 'خارج الدوام', class: 'default' },
      vehicle_issue: { label: 'عطل بالمركبة', class: 'danger' },
      unknown: { label: 'غير محدد', class: 'default' }
    };
    const s = map[status] || { label: status, class: 'default' };
    return <span className={`badge badge--${s.class}`}>{s.label}</span>;
  };

  if (loading) return <div className="page"><div className="page-loading"><div className="spinner" /></div></div>;
  if (!driver) return <div className="page"><div className="page-empty"><h3>السائق غير موجود</h3></div></div>;

  const tabs = [
    { id: 'overview', label: 'نظرة عامة', icon: I.order },
    { id: 'documents', label: 'الوثائق', icon: I.doc, count: documents.length },
    { id: 'vehicles', label: 'المركبات', icon: I.car, count: vehicles.length },
    { id: 'shifts', label: 'الورديات', icon: I.clock },
    { id: 'incentives', label: 'الحوافز', icon: I.star },
    { id: 'leaves', label: 'الإجازات', icon: I.clock },
    { id: 'location', label: 'الموقع', icon: I.map },
    { id: 'reviews', label: 'التقييمات', icon: I.star },
    { id: 'activity', label: 'سجل النشاط', icon: I.order },
  ];

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '1.5rem' }}>
        <button className="btn btn--ghost" onClick={() => navigate('/tenant/drivers')} style={{ alignSelf: 'flex-start' }}>
          {I.back} <span>العودة للسواقين</span>
        </button>
        
        <div className="driver-profile-header">
          <div className="driver-avatar-large">
            {driver.photo_url ? <img src={driver.photo_url} alt="" /> : driver.full_name?.charAt(0)}
          </div>
          <div className="driver-header-info">
            <h1>{driver.full_name}</h1>
            <div className="driver-meta">
              {getStatusBadge(driver.status)}
              {availability && getAvailabilityBadge(availability.availability_status)}
              {driver.rating_avg && (
                <span className="driver-rating">{I.star} {driver.rating_avg.toFixed(1)}</span>
              )}
            </div>
            <div className="driver-contacts">
              <span>{I.phone} {driver.phone}</span>
              {driver.email && <span>{I.mail} {driver.email}</span>}
            </div>
          </div>
          <div className="driver-quick-stats">
            <div className="stat-card">
              <span className="stat-value">{stats?.completed_orders || 0}</span>
              <span className="stat-label">طلب مكتمل</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">{stats?.completion_rate || 0}%</span>
              <span className="stat-label">نسبة الإتمام</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">{stats?.total_revenue?.toLocaleString() || 0}</span>
              <span className="stat-label">الإيرادات (<SAR className="h-[0.75em] w-auto" />)</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">{stats?.avg_delivery_time_mins || 0}</span>
              <span className="stat-label">متوسط التوصيل (د)</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <div className="tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`tab ${activeTab === tab.id ? 'tab--active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.icon}
              <span>{tab.label}</span>
              {tab.count > 0 && <span className="tab-badge">{tab.count}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && (
          <div className="overview-grid">
            {/* Personal Info */}
            <div className="info-card">
              <h3>المعلومات الشخصية</h3>
              <div className="info-list">
                <div className="info-item"><span>الجنسية:</span><span>{driver.nationality || '-'}</span></div>
                <div className="info-item"><span>تاريخ الميلاد:</span><span>{driver.date_of_birth || '-'}</span></div>
                <div className="info-item"><span>رقم الهوية:</span><span>{driver.national_id || '-'}</span></div>
                <div className="info-item"><span>انتهاء الهوية:</span><span>{driver.national_id_expiry || '-'}</span></div>
                <div className="info-item"><span>العنوان:</span><span>{driver.address || '-'}</span></div>
                <div className="info-item"><span>المدينة:</span><span>{driver.city || '-'}</span></div>
              </div>
            </div>

            {/* License Info */}
            <div className="info-card">
              <h3>الرخصة والشهادات</h3>
              <div className="info-list">
                <div className="info-item"><span>رقم الرخصة:</span><span>{driver.license_number || driver.driver_license_number || '-'}</span></div>
                <div className="info-item"><span>نوع الرخصة:</span><span>{driver.license_type || driver.driver_license_type || '-'}</span></div>
                <div className="info-item"><span>انتهاء الرخصة:</span><span>{driver.license_expiry || driver.driver_license_expiry || '-'}</span></div>
                <div className="info-item"><span>الشهادة الصحية:</span><span>{driver.health_certificate_number || '-'}</span></div>
                <div className="info-item"><span>انتهاء الشهادة:</span><span>{driver.health_certificate_expiry || '-'}</span></div>
              </div>
            </div>

            {/* Employment Info */}
            <div className="info-card">
              <h3>بيانات التوظيف</h3>
              <div className="info-list">
                <div className="info-item"><span>تاريخ التوظيف:</span><span>{driver.hire_date || '-'}</span></div>
                <div className="info-item"><span>نوع العقد:</span><span>{driver.contract_type || '-'}</span></div>
                <div className="info-item"><span>نوع الراتب:</span><span>{driver.salary_type || '-'}</span></div>
                <div className="info-item"><span>الراتب الأساسي:</span><span>{driver.base_salary || '-'}</span></div>
                <div className="info-item"><span>عمولة الطلب:</span><span>{driver.commission_per_order || '-'}</span></div>
              </div>
            </div>

            {/* Bank Info */}
            <div className="info-card">
              <h3>البيانات البنكية</h3>
              <div className="info-list">
                <div className="info-item"><span>البنك:</span><span>{driver.bank_name || '-'}</span></div>
                <div className="info-item"><span>رقم الحساب:</span><span>{driver.bank_account || '-'}</span></div>
                <div className="info-item"><span>IBAN:</span><span>{driver.iban || '-'}</span></div>
              </div>
            </div>

            {/* Emergency Contact */}
            <div className="info-card">
              <h3>جهة الاتصال الطوارئ</h3>
              <div className="info-list">
                <div className="info-item"><span>الاسم:</span><span>{driver.emergency_contact_name || '-'}</span></div>
                <div className="info-item"><span>الهاتف:</span><span>{driver.emergency_contact_phone || '-'}</span></div>
              </div>
            </div>

            {/* Current Vehicle */}
            {driver.current_vehicle && (
              <div className="info-card">
                <h3>المركبة الحالية</h3>
                <div className="info-list">
                  <div className="info-item"><span>رقم اللوحة:</span><span>{driver.current_vehicle.plate_number}</span></div>
                  <div className="info-item"><span>النوع:</span><span>{driver.current_vehicle.make} {driver.current_vehicle.model}</span></div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="documents-section">
            <div className="section-header">
              <h3>وثائق السائق ({documents.length})</h3>
              <button className="btn btn--primary btn--sm" onClick={() => setShowDocModal(true)}>{I.plus} إضافة وثيقة</button>
            </div>
            {documents.length === 0 ? (
              <div className="empty-state"><p>لا توجد وثائق مسجلة</p></div>
            ) : (
              <div className="documents-grid">
                {documents.map(doc => (
                  <div key={doc.id} className={`document-card ${doc.is_expired ? 'document-card--expired' : doc.is_expiring_soon ? 'document-card--warning' : ''}`}>
                    <div className="document-icon">{I.doc}</div>
                    <div className="document-info">
                      <h4>{doc.document_type}</h4>
                      <p>{doc.document_number || '-'}</p>
                      <div className="document-dates">
                        <span>الانتهاء: {doc.expiry_date || '-'}</span>
                        {doc.days_to_expiry !== null && (
                          <span className={doc.is_expired ? 'text-danger' : doc.is_expiring_soon ? 'text-warning' : ''}>
                            {doc.is_expired ? 'منتهية' : `${doc.days_to_expiry} يوم`}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className={`badge badge--${doc.verification_status === 'verified' ? 'success' : doc.verification_status === 'rejected' ? 'danger' : 'warning'}`}>
                      {doc.verification_status === 'verified' ? 'موثقة' : doc.verification_status === 'rejected' ? 'مرفوضة' : 'قيد المراجعة'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'vehicles' && (
          <div className="vehicles-section">
            <div className="section-header">
              <h3>المركبات المعينة ({vehicles.length})</h3>
              <button className="btn btn--primary btn--sm" onClick={openVehicleModal}>{I.plus} تعيين مركبة</button>
            </div>
            {vehicles.length === 0 ? (
              <div className="empty-state"><p>لا توجد مركبات معينة</p></div>
            ) : (
              <div className="vehicles-list">
                {vehicles.map(v => (
                  <div key={v.membership_id} className="vehicle-card">
                    <div className="vehicle-icon">{I.car}</div>
                    <div className="vehicle-info">
                      <h4>{v.vehicle.plate_number}</h4>
                      <p>{v.vehicle.make} {v.vehicle.model} - {v.vehicle.year}</p>
                    </div>
                    <div className="vehicle-meta">
                      <span className={`badge badge--${v.is_current ? 'success' : 'default'}`}>
                        {v.is_current ? 'المركبة الحالية' : 'سابقة'}
                      </span>
                      <span className="vehicle-role">{v.role === 'primary' ? 'أساسية' : 'احتياطية'}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'shifts' && (
          <div className="shifts-section">
            <div className="section-header">
              <h3>الورديات المعينة ({shifts.length})</h3>
              <button className="btn btn--primary btn--sm" onClick={openShiftModal}>{I.plus} تعيين وردية</button>
            </div>
            {shifts.length === 0 ? (
              <div className="empty-state"><p>لا توجد ورديات معينة</p></div>
            ) : (
              <div className="shifts-list">
                {shifts.map(s => (
                  <div key={s.id} className="shift-card">
                    <div className="shift-icon">{I.clock}</div>
                    <div className="shift-info">
                      <h4>{s.shift_name}</h4>
                      <p>{s.start_time} - {s.end_time}</p>
                    </div>
                    <span className={`badge badge--${s.is_active ? 'success' : 'default'}`}>
                      {s.is_active ? 'نشطة' : 'غير نشطة'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'incentives' && (
          <div className="incentives-section">
            <div className="section-header">
              <h3>الحوافز والعقوبات</h3>
              <button className="btn btn--primary btn--sm" onClick={() => {/* TODO: Add incentive modal */}}>{I.plus} إضافة</button>
            </div>
            {incentivesSummary && (
              <div className="incentives-summary">
                <div className="summary-card summary-card--success">
                  <span className="summary-value">{incentivesSummary.total_bonuses?.toLocaleString()}{' '}<SAR className="h-[0.8em] w-auto" /></span>
                  <span className="summary-label">إجمالي المكافآت</span>
                </div>
                <div className="summary-card summary-card--danger">
                  <span className="summary-value">{incentivesSummary.total_penalties?.toLocaleString()}{' '}<SAR className="h-[0.8em] w-auto" /></span>
                  <span className="summary-label">إجمالي الخصومات</span>
                </div>
                <div className="summary-card summary-card--primary">
                  <span className="summary-value">{incentivesSummary.net_total?.toLocaleString()}{' '}<SAR className="h-[0.8em] w-auto" /></span>
                  <span className="summary-label">الصافي</span>
                </div>
                <div className="summary-card">
                  <span className="summary-value">{incentivesSummary.month_bonuses?.toLocaleString()}{' '}<SAR className="h-[0.8em] w-auto" /></span>
                  <span className="summary-label">مكافآت هذا الشهر</span>
                </div>
              </div>
            )}
            {incentives.length === 0 ? (
              <div className="empty-state"><p>لا توجد حوافز أو عقوبات</p></div>
            ) : (
              <div className="data-table">
                <table>
                  <thead>
                    <tr>
                      <th>النوع</th>
                      <th>المبلغ</th>
                      <th>السبب</th>
                      <th>الحالة</th>
                      <th>التاريخ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {incentives.map(inc => (
                      <tr key={inc.id}>
                        <td>
                          <span className={`badge badge--${inc.type === 'bonus' || inc.type === 'reward' ? 'success' : 'danger'}`}>
                            {inc.type === 'bonus' ? 'مكافأة' : inc.type === 'reward' ? 'جائزة' : inc.type === 'penalty' ? 'غرامة' : 'خصم'}
                          </span>
                        </td>
                        <td className={inc.type === 'bonus' || inc.type === 'reward' ? 'text-success' : 'text-danger'}>
                          {inc.type === 'bonus' || inc.type === 'reward' ? '+' : '-'}{inc.amount?.toLocaleString()}{' '}<SAR className="h-[0.8em] w-auto" />
                        </td>
                        <td>{inc.reason}</td>
                        <td><span className={`badge badge--${inc.status === 'approved' ? 'success' : inc.status === 'pending' ? 'warning' : 'default'}`}>{inc.status === 'pending' ? 'معلق' : inc.status === 'approved' ? 'مؤكد' : inc.status}</span></td>
                        <td>{new Date(inc.created_at).toLocaleDateString('ar-SA')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'leaves' && (
          <div className="leaves-section">
            <div className="section-header">
              <h3>الإجازات والغياب</h3>
              <button className="btn btn--primary btn--sm" onClick={() => {/* TODO: Add leave modal */}}>{I.plus} طلب إجازة</button>
            </div>
            {leaveBalance && (
              <div className="leave-balance">
                <div className="balance-card">
                  <div className="balance-header">إجازة سنوية</div>
                  <div className="balance-body">
                    <span className="balance-used">{leaveBalance.annual?.used || 0}</span>
                    <span className="balance-sep">/</span>
                    <span className="balance-total">{leaveBalance.annual?.quota || 21}</span>
                  </div>
                  <div className="balance-footer">متبقي: {leaveBalance.annual?.remaining || 21} يوم</div>
                </div>
                <div className="balance-card">
                  <div className="balance-header">إجازة مرضية</div>
                  <div className="balance-body">
                    <span className="balance-used">{leaveBalance.sick?.used || 0}</span>
                    <span className="balance-sep">/</span>
                    <span className="balance-total">{leaveBalance.sick?.quota || 10}</span>
                  </div>
                  <div className="balance-footer">متبقي: {leaveBalance.sick?.remaining || 10} يوم</div>
                </div>
                <div className="balance-card">
                  <div className="balance-header">إجازة بدون راتب</div>
                  <div className="balance-body">
                    <span className="balance-used">{leaveBalance.unpaid_days || 0}</span>
                  </div>
                  <div className="balance-footer">يوم مستخدم</div>
                </div>
              </div>
            )}
            {leaves.length === 0 ? (
              <div className="empty-state"><p>لا توجد إجازات مسجلة</p></div>
            ) : (
              <div className="data-table">
                <table>
                  <thead>
                    <tr>
                      <th>النوع</th>
                      <th>من</th>
                      <th>إلى</th>
                      <th>الأيام</th>
                      <th>السبب</th>
                      <th>الحالة</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaves.map(leave => (
                      <tr key={leave.id}>
                        <td>{leave.leave_type === 'annual' ? 'سنوية' : leave.leave_type === 'sick' ? 'مرضية' : leave.leave_type === 'unpaid' ? 'بدون راتب' : 'طارئة'}</td>
                        <td>{leave.start_date}</td>
                        <td>{leave.end_date}</td>
                        <td>{leave.days} يوم</td>
                        <td>{leave.reason || '-'}</td>
                        <td>
                          <span className={`badge badge--${leave.status === 'approved' ? 'success' : leave.status === 'pending' ? 'warning' : 'danger'}`}>
                            {leave.status === 'approved' ? 'موافق' : leave.status === 'pending' ? 'معلق' : 'مرفوض'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'location' && (
          <div className="location-section">
            <div className="section-header">
              <h3>موقع السائق</h3>
              <button className="btn btn--secondary btn--sm" onClick={loadLocation}>{I.clock} تحديث</button>
            </div>
            {location && location.latitude ? (
              <div className="location-info">
                <div className="location-card">
                  <div className="location-main">
                    <div className="location-icon">{I.map}</div>
                    <div className="location-details">
                      <div className="location-coords">
                        <span>خط العرض: {location.latitude?.toFixed(6)}</span>
                        <span>خط الطول: {location.longitude?.toFixed(6)}</span>
                      </div>
                      <div className="location-meta">
                        {location.speed && <span>السرعة: {location.speed.toFixed(1)} كم/س</span>}
                        {location.battery_level && <span>البطارية: {location.battery_level}%</span>}
                        <span>آخر تحديث: {location.recorded_at ? new Date(location.recorded_at).toLocaleString('ar-SA') : '-'}</span>
                      </div>
                    </div>
                  </div>
                  <a 
                    href={`https://www.google.com/maps?q=${location.latitude},${location.longitude}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn--primary btn--sm"
                  >
                    فتح في خرائط Google
                  </a>
                </div>
                {locationHistory.length > 0 && (
                  <div className="location-history">
                    <h4>سجل المواقع اليوم ({locationHistory.length} نقطة)</h4>
                    <div className="history-list">
                      {locationHistory.slice(0, 10).map((loc, i) => (
                        <div key={i} className="history-item">
                          <span className="history-time">{new Date(loc.recorded_at).toLocaleTimeString('ar-SA')}</span>
                          <span className="history-speed">{loc.speed?.toFixed(1) || 0} كم/س</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="empty-state"><p>لا يوجد موقع مسجل للسائق</p></div>
            )}
          </div>
        )}

        {activeTab === 'reviews' && (
          <div className="reviews-section">
            <div className="section-header">
              <h3>تقييمات العملاء ({reviews.length})</h3>
            </div>
            {reviews.length === 0 ? (
              <div className="empty-state"><p>لا توجد تقييمات</p></div>
            ) : (
              <div className="reviews-list">
                {reviews.map(r => (
                  <div key={r.id} className="review-card">
                    <div className="review-header">
                      <div className="review-rating">
                        {[1,2,3,4,5].map(i => (
                          <span key={i} className={`star ${i <= r.rating ? 'star--filled' : ''}`}>{I.star}</span>
                        ))}
                      </div>
                      <span className="review-date">{new Date(r.created_at).toLocaleDateString('ar-SA')}</span>
                    </div>
                    {r.comment && <p className="review-comment">{r.comment}</p>}
                    <div className="review-meta">
                      <span>{r.customer_name}</span>
                      {r.order_number && <span>طلب #{r.order_number}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'activity' && (
          <div className="activity-section">
            <div className="section-header">
              <h3>سجل الأداء اليومي</h3>
            </div>
            {activityLogs.length === 0 ? (
              <div className="empty-state"><p>لا توجد سجلات</p></div>
            ) : (
              <div className="data-table">
                <table>
                  <thead>
                    <tr>
                      <th>التاريخ</th>
                      <th>الطلبات</th>
                      <th>مكتملة</th>
                      <th>ملغاة</th>
                      <th>الأطنان</th>
                      <th>المسافة</th>
                      <th>الأرباح</th>
                      <th>التقييم</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activityLogs.map(log => (
                      <tr key={log.id}>
                        <td>{log.shift_date}</td>
                        <td>{log.orders_assigned}</td>
                        <td className="text-success">{log.orders_completed}</td>
                        <td className="text-danger">{log.orders_cancelled}</td>
                        <td>{log.total_tons_delivered}</td>
                        <td>{log.total_distance_km} كم</td>
                        <td>{log.earnings_total?.toLocaleString()}{' '}<SAR className="h-[0.8em] w-auto" /></td>
                        <td>{log.average_rating?.toFixed(1) || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Document Modal */}
      {showDocModal && (
        <div className="modal-overlay" onClick={() => setShowDocModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>إضافة وثيقة جديدة</h2>
              <button className="modal-close" onClick={() => setShowDocModal(false)}>{I.x}</button>
            </div>
            <form onSubmit={handleAddDocument}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>نوع الوثيقة *</label>
                    <select value={docForm.document_type} onChange={e => setDocForm({...docForm, document_type: e.target.value})} required>
                      <option value="national_id">الهوية الوطنية</option>
                      <option value="license">رخصة القيادة</option>
                      <option value="health_certificate">الشهادة الصحية</option>
                      <option value="work_permit">تصريح العمل</option>
                      <option value="contract">العقد</option>
                      <option value="residence">الإقامة</option>
                      <option value="vehicle_license">رخصة المركبة</option>
                      <option value="insurance">التأمين</option>
                      <option value="other">أخرى</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>رقم الوثيقة</label>
                    <input type="text" value={docForm.document_number} onChange={e => setDocForm({...docForm, document_number: e.target.value})} placeholder="رقم الوثيقة" />
                  </div>
                  <div className="form-group">
                    <label>تاريخ الإصدار</label>
                    <input type="date" value={docForm.issue_date} onChange={e => setDocForm({...docForm, issue_date: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label>تاريخ الانتهاء</label>
                    <input type="date" value={docForm.expiry_date} onChange={e => setDocForm({...docForm, expiry_date: e.target.value})} />
                  </div>
                  <div className="form-group full-width">
                    <label>رابط الملف</label>
                    <input type="url" value={docForm.file_url} onChange={e => setDocForm({...docForm, file_url: e.target.value})} placeholder="https://..." />
                  </div>
                  <div className="form-group full-width">
                    <label>ملاحظات</label>
                    <textarea value={docForm.notes} onChange={e => setDocForm({...docForm, notes: e.target.value})} rows="2" />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn--secondary" onClick={() => setShowDocModal(false)}>إلغاء</button>
                <button type="submit" className="btn btn--primary" disabled={submitting}>{submitting ? 'جاري الحفظ...' : 'حفظ'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Vehicle Modal */}
      {showVehicleModal && (
        <div className="modal-overlay" onClick={() => setShowVehicleModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>تعيين مركبة</h2>
              <button className="modal-close" onClick={() => setShowVehicleModal(false)}>{I.x}</button>
            </div>
            <form onSubmit={handleAssignVehicle}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label>اختر المركبة *</label>
                    <select value={vehicleForm.vehicle_id} onChange={e => setVehicleForm({...vehicleForm, vehicle_id: e.target.value})} required>
                      <option value="">-- اختر مركبة --</option>
                      {availableVehicles.map(v => (
                        <option key={v.id} value={v.id}>{v.plate_number} - {v.make} {v.model}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group full-width">
                    <label>الدور</label>
                    <select value={vehicleForm.role} onChange={e => setVehicleForm({...vehicleForm, role: e.target.value})}>
                      <option value="primary">أساسية</option>
                      <option value="backup">احتياطية</option>
                    </select>
                  </div>
                </div>
                {availableVehicles.length === 0 && (
                  <div className="empty-state" style={{padding: '1rem'}}><p>لا توجد مركبات متاحة</p></div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn--secondary" onClick={() => setShowVehicleModal(false)}>إلغاء</button>
                <button type="submit" className="btn btn--primary" disabled={submitting || !vehicleForm.vehicle_id}>{submitting ? 'جاري التعيين...' : 'تعيين'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Shift Modal */}
      {showShiftModal && (
        <div className="modal-overlay" onClick={() => setShowShiftModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>تعيين وردية</h2>
              <button className="modal-close" onClick={() => setShowShiftModal(false)}>{I.x}</button>
            </div>
            <form onSubmit={handleAssignShift}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label>اختر الوردية *</label>
                    <select value={shiftForm.shift_id} onChange={e => setShiftForm({...shiftForm, shift_id: e.target.value})} required>
                      <option value="">-- اختر وردية --</option>
                      {availableShifts.map(s => (
                        <option key={s.id} value={s.id}>{s.name} ({s.start_time} - {s.end_time})</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group full-width">
                    <label>أيام العمل</label>
                    <div className="days-selector">
                      {['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'].map((day, i) => (
                        <label
                          key={i}
                          className={`day-checkbox${shiftForm.days_of_week.includes(i) ? ' day-checkbox--selected' : ''}`}
                          onClick={() => {
                            const days = shiftForm.days_of_week.includes(i)
                              ? shiftForm.days_of_week.filter(d => d !== i)
                              : [...shiftForm.days_of_week, i];
                            setShiftForm({...shiftForm, days_of_week: days});
                          }}
                        >
                          <span>{day}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                {availableShifts.length === 0 && (
                  <div className="empty-state" style={{padding: '1rem'}}><p>لا توجد ورديات متاحة. يرجى إنشاء ورديات أولاً.</p></div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn--secondary" onClick={() => setShowShiftModal(false)}>إلغاء</button>
                <button type="submit" className="btn btn--primary" disabled={submitting || !shiftForm.shift_id}>{submitting ? 'جاري التعيين...' : 'تعيين'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .driver-profile-header {
          display: flex;
          align-items: flex-start;
          gap: 1.5rem;
          padding: 1.5rem;
          background: var(--surface);
          border-radius: 12px;
          border: 1px solid var(--border);
        }
        .driver-avatar-large {
          width: 100px;
          height: 100px;
          border-radius: 50%;
          background: linear-gradient(135deg, #3b82f6, #1d4ed8);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2.5rem;
          font-weight: 600;
          flex-shrink: 0;
        }
        .driver-avatar-large img {
          width: 100%;
          height: 100%;
          border-radius: 50%;
          object-fit: cover;
        }
        .driver-header-info {
          flex: 1;
        }
        .driver-header-info h1 {
          margin: 0 0 0.5rem;
          font-size: 1.5rem;
        }
        .driver-meta {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 0.75rem;
        }
        .driver-rating {
          display: flex;
          align-items: center;
          gap: 4px;
          color: #f59e0b;
        }
        .driver-rating svg {
          width: 16px;
          height: 16px;
        }
        .driver-contacts {
          display: flex;
          gap: 1.5rem;
          color: var(--text-secondary);
          font-size: 0.9rem;
        }
        .driver-contacts span {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .driver-contacts svg {
          width: 16px;
          height: 16px;
        }
        .driver-quick-stats {
          display: flex;
          gap: 1rem;
        }
        .stat-card {
          background: var(--background);
          padding: 1rem 1.5rem;
          border-radius: 8px;
          text-align: center;
          min-width: 100px;
        }
        .stat-value {
          display: block;
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--primary);
        }
        .stat-label {
          font-size: 0.75rem;
          color: var(--text-secondary);
        }
        .tabs-container {
          margin: 1.5rem 0;
          border-bottom: 1px solid var(--border);
        }
        .tabs {
          display: flex;
          gap: 0.5rem;
        }
        .tab {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 0.75rem 1rem;
          background: none;
          border: none;
          cursor: pointer;
          color: var(--text-secondary);
          font-size: 0.9rem;
          border-bottom: 2px solid transparent;
          transition: all 0.2s;
        }
        .tab svg {
          width: 18px;
          height: 18px;
        }
        .tab:hover {
          color: var(--text);
        }
        .tab--active {
          color: var(--primary);
          border-bottom-color: var(--primary);
        }
        .tab-badge {
          background: var(--primary);
          color: white;
          font-size: 0.7rem;
          padding: 2px 6px;
          border-radius: 10px;
        }
        .tab-content {
          padding: 1rem 0;
        }
        .overview-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 1rem;
        }
        .info-card {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 1.25rem;
        }
        .info-card h3 {
          margin: 0 0 1rem;
          font-size: 1rem;
          color: var(--text);
        }
        .info-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        .info-item {
          display: flex;
          justify-content: space-between;
          font-size: 0.9rem;
        }
        .info-item span:first-child {
          color: var(--text-secondary);
        }
        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }
        .section-header h3 {
          margin: 0;
        }
        .documents-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 1rem;
        }
        .document-card {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 10px;
        }
        .document-card--expired {
          border-color: #ef4444;
          background: #fef2f2;
        }
        .document-card--warning {
          border-color: #f59e0b;
          background: #fffbeb;
        }
        .document-icon {
          width: 48px;
          height: 48px;
          background: var(--background);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .document-icon svg {
          width: 24px;
          height: 24px;
          color: var(--primary);
        }
        .document-info {
          flex: 1;
        }
        .document-info h4 {
          margin: 0 0 4px;
          font-size: 0.95rem;
        }
        .document-info p {
          margin: 0;
          font-size: 0.85rem;
          color: var(--text-secondary);
        }
        .document-dates {
          display: flex;
          gap: 1rem;
          font-size: 0.8rem;
          margin-top: 4px;
        }
        .text-danger { color: #ef4444; }
        .text-warning { color: #f59e0b; }
        .text-success { color: #22c55e; }
        .vehicles-list, .shifts-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        .vehicle-card, .shift-card {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 10px;
        }
        .vehicle-icon, .shift-icon {
          width: 48px;
          height: 48px;
          background: var(--background);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .vehicle-icon svg, .shift-icon svg {
          width: 24px;
          height: 24px;
          color: var(--primary);
        }
        .vehicle-info, .shift-info {
          flex: 1;
        }
        .vehicle-info h4, .shift-info h4 {
          margin: 0 0 4px;
        }
        .vehicle-info p, .shift-info p {
          margin: 0;
          font-size: 0.85rem;
          color: var(--text-secondary);
        }
        .vehicle-meta {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 4px;
        }
        .vehicle-role {
          font-size: 0.8rem;
          color: var(--text-secondary);
        }
        .reviews-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .review-card {
          padding: 1rem;
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 10px;
        }
        .review-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;
        }
        .review-rating {
          display: flex;
          gap: 2px;
        }
        .star svg {
          width: 16px;
          height: 16px;
          color: #ddd;
        }
        .star--filled svg {
          color: #f59e0b;
        }
        .review-date {
          font-size: 0.8rem;
          color: var(--text-secondary);
        }
        .review-comment {
          margin: 0.5rem 0;
          font-size: 0.9rem;
        }
        .review-meta {
          display: flex;
          gap: 1rem;
          font-size: 0.8rem;
          color: var(--text-secondary);
        }
        .empty-state {
          text-align: center;
          padding: 3rem;
          color: var(--text-secondary);
        }
        .days-selector {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }
        .day-checkbox {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 0.5rem 0.75rem;
          background: var(--background);
          border: 1px solid var(--border);
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;
        }
        .day-checkbox--selected {
          background: var(--tenant-primary, #2563eb) !important;
          color: #fff !important;
          border-color: var(--tenant-primary, #2563eb) !important;
        }
        .day-checkbox input { display: none; }
        /* Incentives Section */
        .incentives-summary {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 1rem;
          margin-bottom: 1.5rem;
        }
        .summary-card {
          padding: 1rem;
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 10px;
          text-align: center;
        }
        .summary-card--success { border-color: #22c55e; background: #f0fdf4; }
        .summary-card--danger { border-color: #ef4444; background: #fef2f2; }
        .summary-card--primary { border-color: var(--primary); background: #eff6ff; }
        .summary-value {
          display: block;
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 4px;
        }
        .summary-label {
          font-size: 0.85rem;
          color: var(--text-secondary);
        }
        /* Leave Balance */
        .leave-balance {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
          gap: 1rem;
          margin-bottom: 1.5rem;
        }
        .balance-card {
          padding: 1rem;
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 10px;
          text-align: center;
        }
        .balance-header {
          font-size: 0.9rem;
          color: var(--text-secondary);
          margin-bottom: 0.5rem;
        }
        .balance-body {
          font-size: 1.75rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
        }
        .balance-used { color: var(--primary); }
        .balance-sep { color: var(--text-secondary); margin: 0 4px; }
        .balance-total { color: var(--text-secondary); }
        .balance-footer {
          font-size: 0.8rem;
          color: var(--text-secondary);
        }
        /* Location Section */
        .location-info {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .location-card {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1.5rem;
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 12px;
          gap: 1rem;
          flex-wrap: wrap;
        }
        .location-main {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .location-icon {
          width: 56px;
          height: 56px;
          background: linear-gradient(135deg, var(--primary), #60a5fa);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .location-icon svg {
          width: 28px;
          height: 28px;
          color: white;
        }
        .location-coords {
          display: flex;
          gap: 1.5rem;
          font-size: 0.95rem;
          margin-bottom: 4px;
        }
        .location-meta {
          display: flex;
          gap: 1rem;
          font-size: 0.85rem;
          color: var(--text-secondary);
          flex-wrap: wrap;
        }
        .location-history {
          padding: 1rem;
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 10px;
        }
        .location-history h4 {
          margin: 0 0 1rem;
          font-size: 0.95rem;
        }
        .history-list {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }
        .history-item {
          display: flex;
          gap: 0.5rem;
          padding: 0.5rem 0.75rem;
          background: var(--background);
          border-radius: 6px;
          font-size: 0.85rem;
        }
        .history-time { color: var(--text-secondary); }
        .history-speed { font-weight: 500; }
        @media (max-width: 768px) {
          .driver-profile-header {
            flex-direction: column;
            align-items: center;
            text-align: center;
          }
          .driver-contacts {
            flex-direction: column;
            align-items: center;
          }
          .driver-quick-stats {
            flex-wrap: wrap;
            justify-content: center;
          }
          .tabs {
            overflow-x: auto;
          }
        }
      `}</style>
    </div>
  );
};

export default DriverDetailPage;
